import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useCursorPagination } from '@frameio/graphql-client';
import type { PageInfo } from '@frameio/graphql-client/generated';
import { useActiveAccountId } from '@frameio/shared/contexts';
import type { ListLoadingStates as LoadingStates } from '@frameio/vapor';
import {
  Button2,
  List,
  ListItem,
  ListSearch,
  Popover,
  Text,
  theme,
} from '@frameio/vapor';
import { IconChevronDown, IconWorkspace } from '@frameio/vapor-icons';
import { useGetWorkspacesForNavigationQuery } from '../../gql/generated/GetWorkspacesForNavigation';
import { useWorkspaceBasicInfoFragment } from '../../gql/generated/WorkspaceBasicInfo';
import { LoadingMoreSkeleton } from './components/LoadingMoreSkeleton';
import { WorkspaceRow } from './components/WorkspaceRow';
import { QUERY_PAGE_SIZE } from './constants';
import TEXT from './generated/TEXT/WorkspaceSelect';

type WorkspaceItem = {
  id: string;
  name: string;
};

type Props = {
  onSelect?: (workspaceId: string) => void;
  selectedWorkspaceId: string;
};

export const WorkspaceSelect = ({ onSelect, selectedWorkspaceId }: Props) => {
  const accountId = useActiveAccountId();
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const { fetchNext, data, previousData, loading, error } = useCursorPagination(
    useGetWorkspacesForNavigationQuery({
      variables: {
        accountId,
        page: { first: QUERY_PAGE_SIZE },
        filter: { query: search },
        order: { field: 'NAME', direction: 'ASC' },
      },
      skip: !accountId,
    })
  );

  const isLoading = loading && !previousData;
  const isLoadingMore = loading && previousData && search.length === 0;

  const workspaces = useMemo(() => {
    return (data?.account?.workspaces?.nodes ?? []).filter(
      (workspace) => workspace.permissions?.canCreateProject
    );
  }, [data]);

  const { data: selectedWorkspace } =
    useWorkspaceBasicInfoFragment(selectedWorkspaceId);

  const handleSelectionChange = (keys: 'all' | Set<string | number>) => {
    if (!(keys instanceof Set) || keys.size === 0) {
      return;
    }
    onSelect?.(String([...keys][0]));
    setIsOpen(false);
  };

  const handleBoundary = (boundary: 'top' | 'bottom') => {
    if (isLoading || boundary === 'top') {
      return;
    }
    const pageInfo = data?.account?.workspaces?.pageInfo;
    if (pageInfo) {
      fetchNext({
        count: QUERY_PAGE_SIZE,
        pageInfo: pageInfo as PageInfo,
      });
    }
  };

  const loadingState = useMemo((): LoadingStates | undefined => {
    if (error) return 'loading-error';
    if (isLoadingMore) return 'loading-more';
    if (isLoading) return 'loading';
    return undefined;
  }, [isLoading, isLoadingMore, error]);

  const renderLoading = (state: LoadingStates): React.ReactNode => {
    if (state === 'loading-more') {
      return <LoadingMoreSkeleton />;
    }
    return null;
  };

  const WorkspaceItems = (
    <ListContainer>
      <List<WorkspaceItem>
        aria-labelledby="workspace-select-list"
        defaultQuery={search}
        defaultSize={LIST_HEIGHT}
        estimatedItemHeight={LIST_ITEM_HEIGHT}
        items={workspaces}
        loading={loadingState}
        onBoundary={handleBoundary}
        onRequestSearch={setSearch}
        onSelectionChange={handleSelectionChange}
        paddingY={LIST_PADDING_Y}
        renderLoading={renderLoading}
        renderPlaceholder={() => (
          <Text
            color="tertiary"
            style={{ padding: theme.spaces[16] }}
            variant="caption"
          >
            {search.length > 0 ? TEXT.noSearchResults : TEXT.noPermissions}
          </Text>
        )}
        scrollToKey={{ id: selectedWorkspaceId, scroll: 'immediate' }}
        search={
          <ListSearch
            autoFocus={true}
            clearButtonAriaLabel={TEXT.clearButtonAriaLabel}
            debounce={LIST_SEARCH_DEBOUNCE_MS}
            pendingStateLabel={TEXT.pendingStateLabel}
            placeholder={TEXT.searchPlaceholder}
            separator={<StyledSeparator />}
            variant="default"
          />
        }
        selectedKeys={[selectedWorkspaceId]}
        selectionMode="single"
        variant="unstyled"
      >
        {(item) => (
          <ListItem key={item.id} as={WorkspaceListItem} textValue={item.name}>
            <WorkspaceRow
              label={item.name}
              searchQuery={search}
              selected={item.id === selectedWorkspaceId}
            />
          </ListItem>
        )}
      </List>
    </ListContainer>
  );

  return (
    <Container>
      <Popover
        content={WorkspaceItems}
        hideOverflow={true}
        isShowing={isOpen}
        onRequestChange={setIsOpen}
        placement="top"
        shouldUsePortal={false}
      >
        <SelectButton
          iconAfter={
            <StyledChevronDown color={theme.colors.icon.secondary} size={16} />
          }
          iconBefore={
            <IconWorkspace color={theme.colors.icon.primary} size={16} />
          }
          onClick={() => setIsOpen(true)}
          variant="tertiary"
        >
          <Text
            color="primary"
            enableTooltip={true}
            truncate={true}
            variant="body"
          >
            {selectedWorkspace?.id ? selectedWorkspace.name : TEXT.placeholder}
          </Text>
        </SelectButton>
      </Popover>
    </Container>
  );
};

TEXT.DEFINE({
  /** Menu label for the workspace selector */
  menuLabel: 'Workspaces',
  /** Placeholder text when no workspace is selected */
  placeholder: 'Select Workspace',
  /** Search input placeholder for filtering workspaces */
  searchPlaceholder: 'Search',
  /** Empty state message when no search results match */
  noSearchResults: 'No results match your current search criteria.',
  /** Message shown when user lacks project creation permission */
  noPermissions:
    "You can't create projects because you don't have permission. Contact your admin for help.",
  /** Aria label for the clear search button */
  clearButtonAriaLabel: 'Clear search input',
  /** Pending state label shown while searching */
  pendingStateLabel: 'Searching',
});

const LIST_HEIGHT = 229;
const LIST_ITEM_HEIGHT = 32;
const LIST_PADDING_Y = 8;
const LIST_SEARCH_DEBOUNCE_MS = 250;

// @note (FIND-2259) I am not crazy about targeting the role=dialog below but there
// does not appear to be a way to otherwise apply any styling to the PopoverContainer.
// I need to set position absolute here as otherwise the attribute is applied after a
// delay which causes a content shift and width=100% is required to match Figma styling.
const Container = styled.div`
  position: relative;
  width: 100%;

  div[role='dialog'] {
    position: absolute;
    width: 100%;
  }
`;

const ListContainer = styled.div`
  display: grid;
  height: ${LIST_HEIGHT}px;
  position: relative;
  width: 100%;
`;

const WorkspaceListItem = styled.div`
  padding: 0 ${theme.spaces[4]};

  &:not(:first-child) {
    padding-top: ${theme.spaces[4]};
  }
`;

const SelectButton = styled(Button2)`
  background: ${theme.colors.background.primary};
  border-radius: ${theme.radii[6]};
  gap: ${theme.spaces[4]};
  justify-content: flex-start;
  padding: ${theme.spaces[4]} ${theme.spaces[8]};
  text-align: left;
  width: 100%;
`;

const StyledChevronDown = styled(IconChevronDown)`
  margin-left: auto;
`;

const StyledSeparator = styled.div`
  background-color: ${theme.colors.border.primary};
  height: 1px;
  margin-left: calc(-1 * ${theme.spaces[12]});
  width: calc(100% + 2 * ${theme.spaces[12]});
`;
